import React, { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";

// Modules
import Reveal from "reveal.js";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  DndContext,
  useDroppable,
  useSensors,
  useSensor,
  PointerSensor,
} from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import { NestedMenuItem } from "mui-nested-menu";
import { debounce } from "lodash";
import interact from "interactjs";

// App
import { serverUrl, sitaEnabled, referenceEnabled, client } from "../../config";
import { getOnlineNode } from "../../core/getNode";
import { postNode, patchNode } from "../../core/postNode";
import { get_csrf_token } from "../../core/auth";
import { default_reveal } from "../../core/defaultReveal";
import SlideEdit from "./partials/slide/edit/slide";
import Toolbar from "./partials/toolbar";
import Sidebar from "./partials/sidebar";
import CategorySelector from "../../partials/categorySelector";
import RevealControls from "./partials/revealControls";
import BespokeMedia from "../../partials/presentations/bespokeMedia";
import ColorPickerNew from "../../partials/presentations/colorPickerNew";
import {
  jsonParser,
  decodeHtmlEntities,
  runAnimation,
  addWidescreen,
  getThemeBackgrounds,
  getThemeJson,
} from "../../utils/helpers";
import AlertModal from "../../partials/alertModal";
import AddAudio from "../../partials/presentations/addAudio";
import { SlideContextProvider } from "../../contexts/slideContext";
import { useSlideConfig } from "../../contexts/slideConfigContext";
import { logCustomEvent } from "../../core/analytics";

// UI components
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import {
  Alert,
  IconButton,
  Button,
  Box,
  Dialog,
  DialogContent,
  Snackbar,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Typography,
  Stack,
  InputLabel,
  FormControl,
  Select,
  MenuItem,
  Autocomplete,
} from "@mui/material";
import {
  ArrowBack,
  Close,
  KeyboardArrowRight,
  ChevronLeft,
} from "@mui/icons-material";
import DialogPaper from "../../partials/dialogPaper";

let revealInstance;

function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height,
  };
}

// The window `resize` event fires continuously while the user drags the window
// edge. EditPresentation holds ~70 pieces of state and renders one <SlideEdit>
// per deck slide plus the <Sidebar>, so calling setState on every resize tick
// re-rendered that whole tree (rebuilding the 80-slide map and re-running the
// Sidebar) dozens of times a second — which locked up the window.
//
// Window size is only ever used to clamp this floating controls dock on-screen,
// so it has no business re-rendering the editor. By owning the window-size
// state here, a resize re-renders ONLY this small wrapper and <RevealControls>;
// the deck and Sidebar are untouched. The controls still track the edge live.
const FloatingRevealControls = ({
  x,
  y,
  elementDimensions,
  setElementDimensions,
  isInitial,
  setIsInitial,
  setX,
  ...revealControlsProps
}) => {
  const [windowSize, setWindowSize] = useState(getWindowDimensions);

  useEffect(() => {
    const handleResize = () => setWindowSize(getWindowDimensions());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <RevealControls
      {...revealControlsProps}
      styles={{
        left: `min(${windowSize.width - 147}px, ${x}px)`,
        top: `min(${windowSize.height - elementDimensions.height}px, ${y}px)`,
      }}
      setDimensions={(size) => {
        setElementDimensions(size);
        if (isInitial) {
          setX(window.innerWidth - (size.width + 30));
          setIsInitial(false);
        }
      }}
    />
  );
};

export default function EditPresentation(props) {
  /** generic */
  const [isLoading, setLoading] = useState(false);
  const [isError, setError] = useState(false);
  const [samplePresentationLoading, setSamplePresentationLoading] =
    useState(true);
  const [authorised, setAuthorised] = useState(true);
  const [userAuth, setUserAuth] = useState({});

  /** slide config context */
  const { configLoading } = useSlideConfig();

  /** errors */
  const [addSlideError, setAddSlideError] = useState(false);
  const [addLibrarySlidesError, setAddLibrarySlidesError] = useState(false);
  const [addCloneSlidesError, setAddCloneSlidesError] = useState(false);
  const [removeSlidesError, setRemoveSlidesError] = useState(false);
  const [slidePatchError, setSlidePatchError] = useState(false);

  // Publishing lock: true while a full OR slide publish is running for this
  // presentation. Editing is blocked while this is set.
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishSlidesSuccess, setPublishSlidesSuccess] = useState(false);
  const [publishSlidesError, setPublishSlidesError] = useState(false);
  const [publishSlidesErrorMsg, setPublishSlidesErrorMsg] = useState("");
  const publishPollRef = useRef(false);

  /** modals */
  const [showPresentationModal, setShowPresentationModal] = useState(false);
  const [showPresentationAudioModal, setShowPresentationAudioModal] =
    useState(false);
  const [showSlideAudioSettings, setShowSlideAudioSettings] = useState(false);
  const [showSlideLayoutSettings, setShowSlideLayoutSettings] = useState(false);
  const [showLogoSettings, setShowLogoSettings] = useState(false);
  const [showBackgroundSettings, setShowBackgroundSettings] = useState(false);
  const [showTransitionSettings, setShowTransitionSettings] = useState(false);
  const [showSlideSettings, setShowSlideSettings] = useState(false);
  const [showHelpDialog, setShowHelpDialog] = useState(false);
  const [showSlideNotes, setShowSlideNotes] = useState(false);

  const [open, setOpen] = useState(false);
  const [showControls, setShowControls] = useState(true);

  const [isInitial, setIsInitial] = useState(true);

  /** Presentation audio */
  const [playSlideAudio, setPlaySlideAudio] = useState(false);
  const [showSlideAudioControls, setShowSlideAudioControls] = useState(false);
  const [playPresentationAudio, setPlayPresentationAudio] = useState(false);
  const [showPresentationAudioControls, setShowPresentationAudioControls] =
    useState(false);
  const [presentationAudioPlaying, setPresentationAudioPlaying] =
    useState(false);

  // Slide audio
  const [slideAudio, setSlideAudio] = useState({});

  // notes
  const [notesObj, setNotesObj] = useState({});

  let [elementDimensions, setElementDimensions] = useState({
    width: 305,
    height: 30,
  });
  /** hooks */
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();

  /** redux */
  // Select the leaf slices directly. Selecting the whole root state and then
  // indexing made react-redux compare the root reference, which combineReducers
  // mints anew on EVERY dispatch app-wide — re-rendering this 3300-line screen
  // on unrelated actions. These selectors re-render only when user/permissions
  // actually change.
  const user = useSelector((state) => state.authReducer.user);
  const permissions = useSelector(
    (state) => state.permissionsReducer.permissions,
  );

  const [csrf_token, setCsrfToken] = useState(user.csrf_token);

  /** reveal */
  const [reveal, setReveal] = useState(null);
  const [indexh, setIndexH] = useState(0);
  // const [indexv, setIndexV] = useState(1);
  const [pendingSlideIndex, setPendingSlideIndex] = useState(null);

  // controls delta
  let [x, setX] = useState(window.innerWidth - (elementDimensions.width + 10));
  let [y, setY] = useState(170);

  /** data */
  const [presentation, setPresentation] = useState(null);
  const [samplePresentation, setSamplePresentation] = useState(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [coreLibrary, setCoreLibrary] = useState(false);
  const [audio, setAudio] = useState(null);
  const [backgroundColor, setBackgroundColor] = useState("#000000");
  const [homeSlide, setHomeSlide] = useState("");
  const [showHomeSlideInput, setShowHomeSlideInput] = useState(false);
  const [thumbnail, setThumbnail] = useState({});
  const [categories, setCategories] = useState([
    { value: 0, label: "All Categories" },
  ]);
  const [languages, setLanguages] = useState([
    { value: 0, label: "All Languages" },
  ]);
  const [themes, setThemes] = useState([{ value: 0, label: "Select a theme" }]);
  const [sitas, setSitas] = useState([]);
  const [references, setReferences] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [selectedLanguage, setSelectedLanguage] = useState(0);
  const [selectedTheme, setSelectedTheme] = useState(0);
  const [selectedThemeItem, setSelectedThemeItem] = useState(null);
  const [selectedSita, setSelectedSita] = useState(null);
  const [selectedReference, setSelectedReference] = useState(null);
  const [slidesToRemove, setSlidesToRemove] = useState([]);
  const [regularIndices, setRegularIndices] = useState([]);
  const [libraryIndices, setLibraryIndices] = useState([]);
  const [excludeIndices, setExcludeIndices] = useState([]);
  const [excludeAlert, setExcludeAlert] = useState(false);
  const [librarySlidesToRemove, setLibrarySlidesToRemove] = useState([]);
  const [corruptSlides, setCorruptsSlides] = useState([]);

  // Per-slide thumbnail mtime, used as the cache-key in <img> URLs so the
  // browser only refetches when the file on disk actually changes.
  const [thumbnailVersions, setThumbnailVersions] = useState({});

  const [isThemeChanged, setThemeChanged] = useState(false);

  // droppable
  const { setNodeRef } = useDroppable({
    id: "droppable",
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
  );

  // get sita
  useEffect(() => {
    if (sitaEnabled) {
      if (sitas) {
        setSita();
      }
    }
    // eslint-disable-next-line
  }, [sitas, presentation]);

  // get references
  useEffect(() => {
    if (referenceEnabled) {
      if (presentation) {
        setReference();
      }
    }
    // eslint-disable-next-line
  }, [presentation]);

  // Seed thumbnail versions whenever the slide list changes. Triggers one bulk
  // status call for any slide IDs we don't already have an mtime for.
  useEffect(() => {
    const slides = presentation?.slides;
    if (!slides || slides.length === 0) return;
    const missing = slides
      .map((s) => s.id)
      .filter((id) => thumbnailVersions[id] === undefined);
    if (missing.length > 0) {
      fetchThumbnailVersions(missing);
    }
    // eslint-disable-next-line
  }, [presentation?.slides]);

  // On mount
  useEffect(() => {
    dispatch({
      type: "TOGGLE_NAVIGATION",
      payload: {
        showNavigation: false,
        manualToggle: false,
      },
    });
    getCategories();

    get_csrf_token().then((response) => {
      setCsrfToken(response.data);
    });

    return () => {
      // Stop any playing audio elements
      document.querySelectorAll("audio").forEach((el) => {
        el.pause();
        el.src = "";
      });

      if (revealInstance) {
        revealInstance.destroy();
        revealInstance = null;
      }
      dispatch({
        type: "TOGGLE_NAVIGATION",
        payload: {
          showNavigation: true,
          manualToggle: false,
        },
      });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize Reveal when slide config is loaded
  useEffect(() => {
    if (!configLoading) {
      initReveal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configLoading]);

  useEffect(() => {
    getAccount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Get content after reveal is initialized
  useEffect(() => {
    if (reveal && reveal !== null) {
      getContent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reveal]);

  // Navigate to pending slide after presentation updates
  useEffect(() => {
    if (pendingSlideIndex !== null && presentation && reveal) {
      setTimeout(() => {
        updateReveal(pendingSlideIndex);
        setPendingSlideIndex(null);
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presentation, pendingSlideIndex]);

  useEffect(() => {
    const handleMouseMove = debounce((event) => {
      setShowControls(true);
      hideRevealControls();
    });
    const hideRevealControls = debounce(() => {
      setShowControls(false);
    }, 5000);

    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  useEffect(() => {
    window.goToSlide = () => {};
    return () => {
      window.goToSlide = undefined;
    };
    // eslint-disable-next-line
  }, [reveal]);

  // EditorJS renders its toolbar (+ / tune toggler) and tune popover into a shared,
  // body-level `.ce-popover-holder` (outside React and outside each editor's DOM), so it can
  // escape transformed / clipped slide ancestors. If we leave the edit page while a popover is
  // open, that holder survives the route change and the tune buttons linger on the next screen.
  // Clear it on unmount; `getPopoverHolder()` recreates it lazily on the next editor interaction.
  useEffect(() => {
    return () => {
      document
        .querySelectorAll(".ce-popover-holder")
        .forEach((holder) => holder.remove());
    };
  }, []);

  // Keep the editor locked whenever the server says this presentation is being
  // published (e.g. a publish kicked off elsewhere, or the page was reloaded
  // mid-publish). We long-poll publishing_progress, then refetch so the lock
  // clears the moment publishing finishes.
  useEffect(() => {
    if (presentation?.is_publishing === true) {
      setIsPublishing(true);

      if (!publishPollRef.current) {
        publishPollRef.current = true;
        getOnlineNode(`/api/publishing_progress/${params.id}`, { timeout: 0 })
          .catch(() => {})
          .finally(() => {
            publishPollRef.current = false;
            getContent(indexh);
          });
      }
    } else if (presentation) {
      setIsPublishing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presentation?.is_publishing]);

  useEffect(() => {
    interact(".ps-sidebar").resizable({
      edges: { right: true },
      allowFrom: ".ps-sidebar-resize-container",
      modifiers: [
        interact.modifiers.restrictSize({
          min: { width: 150 },
          max: { width: 300 },
        }),
      ],
      listeners: {
        move(event) {
          Object.assign(event.target.style, {
            width: `${event.rect.width}px`,
          });
        },
      },
    });
    interact(".ps-sidebar-resize-container").on("tap", (event) => {
      if (event.target.closest(".ps-sidebar").style.width === "0px") {
        Object.assign(event.target.closest(".ps-sidebar").style, {
          width: "260px",
        });
      } else {
        Object.assign(event.target.closest(".ps-sidebar").style, {
          width: "0px",
        });
      }
    });
  }, []);

  const initReveal = () => {
    revealInstance = new Reveal({
      width: 1366,
      height: 768,
      margin: 0,
      // Slides are full-bleed 1366x768 canvases, so vertical centering is a
      // visual no-op — but with center:true reveal's layout() reads
      // slide.scrollHeight on every window-resize tick (right after writing
      // .slides width/height), forcing a synchronous reflow of the heavy active
      // EditorJS/background subtree. center:false skips that read entirely.
      center: false,
      slideNumber: false,
      help: false,
      transition: "slide",
      progress: false,
      hashOneBasedIndex: true,
      respondToHashChanges: true,
      hash: true,
      maxScale: 1,
      minScale: 0.5,
      controls: false,
      controlsTutorial: false,
      overview: false,
      fragmentInURL: true,
    });

    revealInstance
      .initialize()
      .then(() => {
        const indices = revealInstance.getIndices(
          revealInstance.getCurrentSlide(),
        );
        const currentIndex = indices.h ? indices.h : 0;

        setIndexH(currentIndex);
        setLoading(false);
        setReveal(revealInstance);

        const currentSlide = revealInstance.getCurrentSlide();
        checkAdvancedSlide(currentSlide);

        revealInstance.on("slidechanged", (event) => {
          setIndexH(event.indexh);
          // setIndexV(event.indexv);
          checkAdvancedSlide(event.currentSlide);
        });

        revealInstance.on("slidetransitionend", (event) => {
          // if (document.querySelector(".slides")) {
          //   const allIframes = document.querySelectorAll(
          //     ".slides iframe.advanced-slide"
          //   );
          //   allIframes.forEach((iframe) => {
          //     addWidescreen(iframe);
          //   });
          // }
        });
      })
      .catch(console.error);
  };

  const updateReveal = (index) => {
    if (reveal) {
      reveal.sync();
      reveal.slide(index);

      const currentSlide = reveal.getCurrentSlide();
      checkAdvancedSlide(currentSlide);
    }
  };

  const navigateTo = (indexh, indexv) => {
    if (reveal) {
      reveal.slide(indexh);
    }
  };

  const checkAdvancedSlide = (currentSlide) => {
    const allIframes = document.querySelectorAll(
      ".slides iframe.advanced-slide",
    );
    allIframes.forEach((iframe) => {
      addWidescreen(iframe);
    });

    let currentIframe;

    if (currentSlide) {
      try {
        currentIframe = currentSlide.querySelector("iframe.advanced-slide");
      } catch {
        //
      }
    }

    if (currentIframe) {
      if (currentIframe.dataset.slideReady === "true") {
        // New slide with message passing - tell it to run directly
        currentIframe.contentWindow.postMessage({ type: "RUN_ANIMATION" }, "*");
      } else {
        // Legacy slide - fall back to polling
        runAnimation(currentIframe);
      }
    }
  };

  const getCategories = () => {
    getOnlineNode(
      "api/categories?vid=presentation_categories,language,themes,sita,reference&creator=true&used=1",
    )
      .then((response) => {
        let _categories = [
          {
            value: 0,
            label: "All Categories",
            has_create_permission: true,
            has_view_permission: true,
            has_content: true,
          },
        ];
        let _languages = [{ value: 0, label: "All Languages" }];
        let _themes = [{ value: 0, label: "Select a theme" }];
        let _sitas = [{ value: "0", title: "NONE", tid: "0", label: "NONE" }];
        let _references = [
          { value: "0", title: "NONE", tid: "0", label: "NONE" },
        ];

        response.data.forEach((item) => {
          if (item.has_view_permission === true) {
            if (item.vid === "presentation_categories") {
              _categories.push({
                ...item,
                value: item.tid,
                label: item.title,
              });
            }

            if (item.vid === "language") {
              _languages.push({
                ...item,
                value: item.tid,
                label: item.title,
              });
            }

            if (item.vid === "themes") {
              _themes.push(item);
            }

            if (item.vid === "sita") {
              _sitas.push({
                ...item,
                value: item.tid,
                label: item.title,
              });
            }

            if (item.vid === "reference") {
              _references.push({
                ...item,
                value: item.tid,
                label: item.title,
              });
            }
          }
        });

        setThemes(_themes);
        setLanguages(_languages);
        setCategories(_categories);
        setSitas(_sitas);
        setReferences(_references);
      })
      .catch(console.error);
  };

  const setSita = () => {
    if (sitaEnabled) {
      let selected;

      if (presentation?.sita?.tid) {
        selected = sitas.find((item) => item.tid === presentation.sita?.tid);
      } else {
        selected = sitas.find((item) => item.tid === 0);
      }

      setSelectedSita(selected);
    }
  };

  const setReference = () => {
    if (referenceEnabled) {
      let selected;

      if (presentation?.reference?.tid) {
        selected = references.find(
          (item) => item.tid === presentation.reference?.tid,
        );
      } else {
        selected = references.find((item) => item.tid === 0);
      }

      setSelectedReference(selected);
    }
  };

  const getSamplePresentation = (tid) => {
    if (tid && tid !== selectedTheme) {
      setSamplePresentationLoading(true);
      getOnlineNode(`api/sample_presentation/${tid}`)
        .then((response) => {
          setSamplePresentation(response.data);
          setSamplePresentationLoading(false);
        })
        .catch((error) => {
          setSamplePresentationLoading(false);
        });
    }
  };

  /**
   * @function getContent - Retrieves presentation data from server
   *
   * @param {Boolean} isUpdateOnly
   * @param {Boolean} revealIndex
   */
  const getContent = async (revealIndex) => {
    if (params?.id) {
      getOnlineNode(`api/presentation/${params.id}?status=false`)
        .then((response) => {
          if (response.data) {
            if (response.data?.has_edit_permission === true) {
              logCustomEvent(
                "read",
                {
                  title: response.data.title,
                  nid: response.data.id,
                },
                "PresentationEdit",
              );

              if (response?.data?.title) {
                setTitle(response?.data?.title);
              }

              if (response?.data?.body) {
                setBody(response?.data?.body);
              }

              if (response?.data?.core_library) {
                setCoreLibrary(
                  response?.data?.core_library === "1" ? true : false,
                );
              }

              const json = response?.data?.json;

              if (json?.audio) {
                setAudio(json.audio);
              }

              if (json?.thumbnail) {
                setThumbnail(json.thumbnail);
              }

              if (json?.backgroundColor) {
                setBackgroundColor(json.backgroundColor);
              } else if (response?.data?.theme.label) {
                const themeJson = getThemeJson(response?.data?.theme.label);
                if (themeJson && themeJson?.defaultBackgroundColor) {
                  setBackgroundColor(themeJson.defaultBackgroundColor);
                }
              }

              if (json?.homeSlide) {
                setHomeSlide(json.homeSlide);
                setShowHomeSlideInput(true);
              }

              if (json?.notes) {
                setNotesObj(json.notes);
              }

              if (json?.slideAudio) {
                setSlideAudio(json.slideAudio);
              }

              setSelectedCategory(
                response.data.category.tid
                  ? parseInt(response.data.category.tid, 10)
                  : 0,
              );
              setSelectedLanguage(
                response.data.language.tid
                  ? parseInt(response.data.language.tid, 10)
                  : 0,
              );

              if (sitaEnabled) {
                setSelectedSita(
                  response.data.sita.tid
                    ? parseInt(response.data.sita.tid, 10)
                    : 0,
                );
              }

              if (referenceEnabled) {
                setSelectedReference(
                  response.data.reference.tid
                    ? parseInt(response.data.reference.tid, 10)
                    : 0,
                );
              }

              setSelectedTheme(
                response.data.theme.tid
                  ? parseInt(response.data.theme.tid, 10)
                  : 0,
              );
              setSelectedThemeItem(
                response.data.theme.tid
                  ? {
                      label: response.data.theme.label,
                      value: response.data.theme.tid,
                    }
                  : {
                      label: "Select a theme",
                      value: 0,
                    },
              );

              getSamplePresentation(response.data.theme.tid);
              setPresentation(response.data);
              setLoading(false);

              const safeRevealIndex = Math.max(0, revealIndex ?? 0);
              setPendingSlideIndex(safeRevealIndex);
            } else {
              setLoading(false);
              setAuthorised(false);
            }
          } else {
            setLoading(false);
            setError(true);
          }
        })
        .catch((error) => {
          if (typeof error === "string") {
            if (error.indexOf("SecurityError") > -1) {
              setLoading(false);
            } else {
              setLoading(false);
              setError(true);
            }
          } else {
            setLoading(false);
            setError(true);
          }
        });
    } else {
      setLoading(false);
      setError(true);
    }
  };

  /**
   * @function updatePresentation - Send patch to server to update presentation entity
   *
   * @param {Array} new_slides - Array of slide ID's
   */
  const updatePresentation = (new_slides, _notesObj, _slideAudio, options = {}) => {
    return new Promise((resolve, reject) => {
      let data = {
        id: presentation.id,
        title: title,
        body: body,
        category: selectedCategory,
        language: selectedLanguage,
        core_library: coreLibrary ? 1 : 0,
        theme: selectedTheme,
        json: {
          audio: audio,
          thumbnail: thumbnail,
          backgroundColor: backgroundColor,
          homeSlide: homeSlide ? homeSlide : "",
        },
      };

      if (sitaEnabled) {
        data.sita = selectedSita?.tid ? selectedSita.tid : 0;
      }

      if (referenceEnabled) {
        data.reference = selectedReference?.tid ? selectedReference.tid : 0;
      }

      if (new_slides) {
        data.slides = new_slides;
      }

      if (_notesObj || notesObj) {
        data.json.notes = _notesObj || notesObj;
        data.json.notes = processEmptyNotes(data.json.notes);
      }

      if (_slideAudio || slideAudio) {
        data.json.slideAudio = _slideAudio || slideAudio;
      }

      let exSlides;

      if (isThemeChanged) {
        exSlides = excludeSlides();
        if (exSlides.excludedSlides.length > 0) {
          data.exclude_slide_ids = exSlides.excludedSlides;
        }
      }

      patchNode(`api/presentation/${params.id}`, data, csrf_token)
        .then((response) => {
          if (isThemeChanged) {
            if (exSlides?.excludedIndices.length > 0) {
              setExcludeIndices(exSlides?.excludedIndices);
              setExcludeAlert(true);
              resolve(response);
            } else {
              window.location.reload();
            }
          } else {
            // Notes/audio live in presentation.json (not on slide objects), so
            // callers that only changed those can pass { skipRefetch: true } and
            // update local state optimistically — avoiding the getContent refetch
            // that mints a brand-new presentation + 80 new slide objects and
            // re-renders the entire deck.
            if (!options.skipRefetch) {
              setLoading(true);
              getContent(indexh);
            }
            resolve(response);
          }
        })
        .catch((error) => {
          setLoading(false);
          reject(error);
        });
    });
  };

  const excludeSlides = () => {
    let slideArr = presentation?.slides.map((e, index) => {
      return {
        id: e.id,
        index: index + 1,
        initial_presentation: e.initial_presentation,
        json: jsonParser(e.slide_json),
      };
    });
    let selectedThemeLayouts = getThemeJson(selectedThemeItem.title)?.layouts;
    let excludedSlides = [];
    let excludedIndices = [];
    slideArr.forEach((e) => {
      let matched = selectedThemeLayouts.find(
        (f) => f.id === e.json?.reveal?.slideLayout.id,
      );
      if (e.initial_presentation !== presentation?.id) {
        excludedSlides.push(e.id);
        excludedIndices.push(e.index);
      } else if (!matched) {
        excludedSlides.push(e.id);
        excludedIndices.push(e.index);
      }
    });

    return { excludedSlides: excludedSlides, excludedIndices: excludedIndices };
  };

  /**
   * Rename a slide from outside the active-slide editor flow (e.g. the slide
   * management modal). Persists the new title and patches local state so the
   * sidebar/grid reflect the change without a full reload. Returns a promise
   * so callers can show a saving indicator until the request settles.
   */
  const renameSlide = (slideId, newTitle) => {
    if (!presentation || !slideId) return Promise.resolve();
    const data = {
      id: slideId,
      node_data: { title: newTitle },
    };
    return patchNode("api/slide/" + slideId, data, csrf_token)
      .then(() => {
        setPresentation((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            slides: prev.slides.map((s) =>
              s.id === slideId ? { ...s, title: newTitle } : s,
            ),
          };
        });
      })
      .catch((error) => {
        console.error("Failed to rename slide", error);
        throw error;
      });
  };

  /**
   * Reorder slides without unmounting the sidebar / management modal.
   * Patches the new order to the server, then reshuffles the local
   * presentation.slides array in place. No loading spinner, no full refetch
   * — the caller already shows an optimistic order.
   */
  const reorderSlidesQuietly = (newOrderIds, newActiveIndex) => {
    if (!presentation || !Array.isArray(newOrderIds)) {
      return Promise.resolve();
    }
    return updatePresentation(newOrderIds).then(() => {
      // flushSync forces React to commit the reordered <SlideEdit> list to
      // the DOM before we call reveal.sync(). Without this, sync() runs
      // against stale section order and reveal keeps navigating the old deck.
      // SlideEdit is keyed by slide.id, so this commit physically moves the
      // <section> nodes into the new order rather than just swapping props.
      flushSync(() => {
        setPresentation((prev) => {
          if (!prev) return prev;
          const byId = new Map(prev.slides.map((s) => [s.id, s]));
          const next = newOrderIds.map((id) => byId.get(id)).filter(Boolean);
          // Defensive: append any slides missing from the order list so we
          // don't silently drop them.
          prev.slides.forEach((s) => {
            if (!newOrderIds.includes(s.id)) next.push(s);
          });
          return { ...prev, slides: next };
        });
      });

      const targetIndex = Number.isInteger(newActiveIndex)
        ? Math.max(0, newActiveIndex)
        : indexh;

      if (reveal) {
        reveal.sync();
        reveal.slide(targetIndex);
        checkAdvancedSlide(reveal.getCurrentSlide());
      }
      setIndexH(targetIndex);
    });
  };

  /**
   * @function addSlide - Adds a new slide to the presentation
   *
   * @param {Object} slide - Slide entity from sample presentation
   * @param {Object} layout - Layout json
   * @param {Boolean} _index
   */
  const addSlide = (slide, layout, _index) => {
    setLoading(true);

    let data;

    if (slide && slide?.id) {
      data = {
        presentation_id: presentation.id,
        slides: [slide.id],
        should_keep_current_theme: "1",
        // should_prevent_duplicate: layout.id === 'custom' ? "1" : "0"
      };

      postNode("api/slide_clone", data, csrf_token)
        .then((response) => {
          if (response.data.length > 0) {
            let new_slides = [];

            presentation.slides.forEach((_slide) => {
              new_slides.push(_slide.id);
            });

            new_slides.splice(_index, 0, ...response.data);

            updatePresentation(new_slides)
              .then((response) => {
                setPresentation(null);
                getContent(_index);
              })
              .catch((error) => {
                setLoading(false);
              });
          } else {
            setLoading(false);
          }
        })
        .catch((error) => {
          setLoading(false);
          setAddSlideError(true);
        });
    } else {
      if (layout && layout.id) {
        addSlideFromLayout(layout, _index);
      } else {
        setLoading(true);
        setAddSlideError(true);
      }
    }
  };

  const addSlideFromLayout = (layout, _index) => {
    let data = {
      _links: {
        type: {
          href: serverUrl + "/rest/type/slide/slide",
        },
      },
      title: {
        value: "New slide for " + presentation.id,
      },
      subtitle: {
        value: "",
      },
      initial_presentation: [
        {
          target_id: presentation.id,
        },
      ],
    };

    if (presentation.theme.tid) {
      data.theme = [
        {
          target_id: presentation.theme.tid,
        },
      ];
    }

    postNode("entity/slide", data, csrf_token)
      .then((response) => {
        const new_slide_id = response.data?.id[0]?.value;

        if (new_slide_id) {
          let reveal = default_reveal;

          reveal.slideLayout = {
            id: layout.id,
            name: layout.name,
            files: layout?.files,
          };

          const slideData = {
            id: new_slide_id,
            presentation_id: presentation.id,
            json: {
              blocks: [],
              reveal: default_reveal,
            },
          };

          patchNode("api/slide/" + new_slide_id, slideData, csrf_token)
            .then((_response) => {
              let new_slides = [];

              presentation.slides.forEach((_slide) => {
                new_slides.push(_slide.id);
              });

              new_slides.splice(_index, 0, new_slide_id);

              updatePresentation(new_slides)
                .then((response) => {
                  setPresentation(null);
                  getContent(_index);
                })
                .catch((error) => {
                  setLoading(false);
                });
            })
            .catch((error) => {
              setLoading(false);
              setAddSlideError(true);
            });
        } else {
          setLoading(false);
        }
      })
      .catch((error) => {
        setLoading(false);
        setAddSlideError(true);
      });
  };

  /**
   * @function addLibrarySlides - Adds an array of new slides to presentation entity
   *
   * @param {Array} new_slides - Array of slide ID's
   * @param {Number} index - The pre index of the slides to add
   */
  const addLibrarySlides = (new_slides, _index, _notesObj, _slideAudio) => {
    if (new_slides.length > 0) {
      setLoading(true);

      let existing_slides = [];
      let tempNotesObj = null;
      let tempSlideAudio = null;

      presentation.slides.forEach((slide) => {
        existing_slides.push(slide.id);
      });

      existing_slides.splice(_index, 0, ...new_slides);

      if (_notesObj !== null) {
        if (Object.keys(_notesObj).length > 0) {
          tempNotesObj = { ...notesObj, ..._notesObj };
          setNotesObj(tempNotesObj);
        }
      }

      if (_slideAudio !== null) {
        if (Object.keys(_slideAudio).length > 0) {
          tempSlideAudio = { ...slideAudio, ..._slideAudio };
          setSlideAudio(tempSlideAudio);
        }
      }

      updatePresentation(existing_slides, tempNotesObj, tempSlideAudio)
        .then((response) => {
          setPresentation(null);
          getContent(_index);
        })
        .catch(() => {
          setLoading(false);
          setAddLibrarySlidesError(true);
        });
    }
  };

  /**
   * @function addCloneSlides - Clones an array of slides to presentation entity
   *
   * @param {Array} new_slides - Array of slide ID's
   */
  const addCloneSlides = (
    new_slides,
    _index,
    _notesObj,
    _slideAudio,
    _unpublished,
  ) => {
    let data;

    if (new_slides.length > 0) {
      setLoading(true);

      data = {
        presentation_id: presentation.id,
        slides: new_slides,
        should_keep_current_theme: "1",
      };

      if (_unpublished) {
        data.isDuplicateFromSidebar = "1";
      }

      postNode("api/slide_clone", data, csrf_token)
        .then((response) => {
          if (response.data.length > 0) {
            let existing_slides = [];
            let tempNotesObj = null;
            let tempSlideAudio = null;

            presentation.slides.forEach((slide) => {
              existing_slides.push(slide.id);
            });

            if (_notesObj && Object.keys(_notesObj).length > 0) {
              tempNotesObj = { ...notesObj };
              if (
                Array.isArray(response.data) &&
                response.data.length >= new_slides.length
              ) {
                new_slides.forEach((e, i) => {
                  if (_notesObj.hasOwnProperty(e)) {
                    tempNotesObj[response.data[i]] = _notesObj[e];
                  }
                });
              }
            }

            if (_slideAudio && Object.keys(_slideAudio).length > 0) {
              tempSlideAudio = { ...slideAudio };
              if (
                Array.isArray(response.data) &&
                response.data.length >= new_slides.length
              ) {
                new_slides.forEach((e, i) => {
                  if (_slideAudio.hasOwnProperty(e)) {
                    tempSlideAudio[response.data[i]] = _slideAudio[e];
                  }
                });
              }
            }

            existing_slides.splice(_index, 0, ...response.data);

            updatePresentation(existing_slides, tempNotesObj, tempSlideAudio)
              .then((response) => {
                setPresentation(null);
                getContent(_index);
              })
              .catch(() => {
                setLoading(false);
                setAddCloneSlidesError(true);
              });
          } else {
            setLoading(false);
          }
        })
        .catch(() => {
          setLoading(false);
          setAddCloneSlidesError(true);
        });
    }
  };
  const addTranslatedSlides = (
    new_slides,
    _language,
    _index,
    _notesObj,
    _slideAudio,
    _unpublished,
  ) => {
    let data;

    if (new_slides.length > 0) {
      setLoading(true);

      data = {
        presentation_id: presentation.id,
        slides: new_slides,
        should_keep_current_theme: "1",
        language: _language,
      };

      if (_unpublished) {
        data.isDuplicateFromSidebar = "1";
      }

      postNode("api/v2/ai/slide/translator", data, csrf_token)
        .then((response) => {
          if (response.data.length > 0) {
            let existing_slides = [];
            let tempNotesObj = null;
            let tempSlideAudio = null;

            presentation.slides.forEach((slide) => {
              existing_slides.push(slide.id);
            });

            if (_notesObj && Object.keys(_notesObj).length > 0) {
              tempNotesObj = { ...notesObj };
              if (
                Array.isArray(response.data) &&
                response.data.length >= new_slides.length
              ) {
                new_slides.forEach((e, i) => {
                  if (_notesObj.hasOwnProperty(e)) {
                    tempNotesObj[response.data[i]] = _notesObj[e];
                  }
                });
              }
            }

            if (_slideAudio && Object.keys(_slideAudio).length > 0) {
              tempSlideAudio = { ...slideAudio };
              if (
                Array.isArray(response.data) &&
                response.data.length >= new_slides.length
              ) {
                new_slides.forEach((e, i) => {
                  if (_slideAudio.hasOwnProperty(e)) {
                    tempSlideAudio[response.data[i]] = _slideAudio[e];
                  }
                });
              }
            }

            existing_slides.splice(_index, 0, ...response.data);

            updatePresentation(existing_slides, tempNotesObj, tempSlideAudio)
              .then((response) => {
                setPresentation(null);
                getContent(_index);
              })
              .catch(() => {
                setLoading(false);
                setAddCloneSlidesError(true);
              });
          } else {
            setLoading(false);
          }
        })
        .catch((error) => {
          console.error(error);
          setLoading(false);
          setAddCloneSlidesError(true);
        });
    }
  };

  const toggleSlideStatus = (slides) => {
    setLoading(true);

    let slides_to_update = [];

    if (slides.length > 0) {
      presentation.slides.forEach((slide) => {
        const found = slides.some((item) => item === slide?.id);

        if (found) {
          slides_to_update.push({
            id: slide.id,
            status: slide.published === true ? 0 : 1,
          });
        }
      });

      postNode("/slides_draft", slides_to_update, csrf_token)
        .then((_response) => {
          getContent(indexh);
        })
        .catch((_error) => {
          setLoading(false);
          setSlidePatchError(true);
        });
    } else {
      setLoading(false);
    }
  };

  const fetchThumbnailVersions = (ids) => {
    if (!ids || ids.length === 0) return Promise.resolve({});
    const list = ids.join(",");
    return getOnlineNode(
      `api/thumbnail_status?slides=${list}&_format=json`,
    )
      .then((response) => {
        const data = response?.data || {};
        setThumbnailVersions((prev) => ({ ...prev, ...data }));
        return data;
      })
      .catch(() => ({}));
  };

  // Coalesced poll loop:
  // - Single timer at any time, regardless of how many regen actions fire.
  // - Per-slide baseline + deadline tracked in a ref so subsequent calls
  //   merge into the in-flight loop instead of spawning new ones.
  // - Skips the network call when the tab is hidden (keeps the loop alive
  //   so it picks up as soon as the tab is visible again).
  // - Cleaned up on unmount via the effect below.
  // 5s interval, 60s per-slide deadline — accounts for the tsp queue
  // (10 concurrent slots) which can serialise bursts of regens.
  const POLL_INTERVAL = 5000;
  const POLL_DEADLINE = 60000;
  const pollStateRef = useRef({ pending: new Map(), timerId: null });

  const runPollTick = () => {
    const state = pollStateRef.current;
    state.timerId = null;

    // Tab not visible — reschedule without hitting the network.
    if (typeof document !== "undefined" && document.hidden) {
      if (state.pending.size > 0) {
        state.timerId = setTimeout(runPollTick, POLL_INTERVAL);
      }
      return;
    }

    // Drop entries past their deadline.
    const now = Date.now();
    for (const [id, entry] of state.pending) {
      if (now > entry.deadline) state.pending.delete(id);
    }
    if (state.pending.size === 0) return;

    const ids = Array.from(state.pending.keys());
    fetchThumbnailVersions(ids).then((data) => {
      for (const id of ids) {
        const entry = state.pending.get(id);
        const next = data[id];
        if (entry && next != null && next !== entry.baseline) {
          state.pending.delete(id);
        }
      }
      if (state.pending.size > 0 && !state.timerId) {
        state.timerId = setTimeout(runPollTick, POLL_INTERVAL);
      }
    });
  };

  const pollThumbnailRefresh = (slideIds) => {
    if (!slideIds || slideIds.length === 0) return;
    const state = pollStateRef.current;
    const deadline = Date.now() + POLL_DEADLINE;

    slideIds.forEach((id) => {
      const existing = state.pending.get(id);
      if (existing) {
        // Already being polled — refresh the deadline so the new regen
        // action gets the full window.
        existing.deadline = deadline;
      } else {
        state.pending.set(id, {
          baseline: thumbnailVersions[id] ?? null,
          deadline,
        });
      }
    });

    if (!state.timerId) {
      state.timerId = setTimeout(runPollTick, POLL_INTERVAL);
    }
  };

  // Cancel any in-flight poll timer on unmount so we don't keep polling
  // a dead component.
  useEffect(() => {
    return () => {
      const state = pollStateRef.current;
      if (state.timerId) {
        clearTimeout(state.timerId);
        state.timerId = null;
      }
      state.pending.clear();
    };
  }, []);

  const generateThumbnails = (slides) => {
    if (!slides || slides.length === 0) return;
    const ids = slides.join();
    getOnlineNode(`api/generate_thumbnails/${params.id}?slides=${ids}`).catch(
      () => {},
    );
    pollThumbnailRefresh(slides);
  };

  /**
   * @function onPublishSlides - Publishes the selected slides into the already
   * published deck via the partial-publish endpoint. The request blocks until
   * the server finishes, so we lock the editor for the duration and refetch the
   * (now published) presentation afterwards.
   *
   * @param {Array} slides - Array of slide ID's to publish
   */
  const onPublishSlides = (slides) => {
    if (!slides || slides.length === 0) return;
    if (isPublishing) return;

    setIsPublishing(true);
    const ids = slides.join(",");

    getOnlineNode(
      `/api/v2/presentation/publish-slides/${params.id}?slide_ids=${ids}`,
      { timeout: 0 },
    )
      .then(() => {
        setPublishSlidesSuccess(true);
        // Refetch so book state / status reflect the publish. The is_publishing
        // effect will settle the lock once fresh data arrives.
        getContent(indexh);
      })
      .catch((error) => {
        const msg =
          error?.response?.data?.error ||
          "Failed to publish slide(s). Please try again.";
        setPublishSlidesErrorMsg(msg);
        setPublishSlidesError(true);
      })
      .finally(() => {
        setIsPublishing(false);
      });
  };

  const getAccount = () => {
    if (typeof user.current_user === "undefined") {
      navigate("/");
    } else {
      let path = `api/v2/users?uid=${user.current_user.uid}`;
      getOnlineNode(path)
        .then((response) => {
          if (response.data.rows[0]) {
            setUserAuth(response.data.rows[0]);
          } else {
            setError(true);
            setLoading(false);
          }
        })
        .catch((_error) => {
          setError(true);
        });
    }
  };

  /**
   * @function removeSlides - Removed an array of slides from the presentation entity
   *
   * @param {Array} slides - Array of slide ID's
   */
  const removeSlides = (slides) => {
    if (slides.length > 0) {
      let new_slides = [];
      let tempNotesObj = null;
      let tempSlideAudio = null;

      presentation.slides.forEach((slide) => {
        const found = slides.some((item) => item === slide?.id);

        if (!found) {
          new_slides.push(slide.id);
        }
      });

      if (notesObj !== null && Object.keys(notesObj).length > 0) {
        tempNotesObj = { ...notesObj };
        slides.forEach((e) => {
          if (tempNotesObj.hasOwnProperty(e)) {
            delete tempNotesObj[e];
          }
        });
      }

      if (slideAudio !== null && Object.keys(slideAudio).length > 0) {
        tempSlideAudio = { ...slideAudio };
        slides.forEach((e) => {
          if (tempSlideAudio.hasOwnProperty(e)) {
            delete tempSlideAudio[e];
          }
        });
      }

      updatePresentation(new_slides, tempNotesObj, tempSlideAudio)
        .then((response) => {
          setPresentation(null);
          setIndexH(indexh);
          getContent(indexh - 1);
        })
        .catch(() => {
          setLoading(false);
          setRemoveSlidesError(true);
        });
    } else {
      setRemoveSlidesError(true);
      setLoading(false);
    }
  };
  const handleThemeSelection = (tid) => {
    let themeItem = themes.filter((item) => {
      return item.tid === tid;
    });

    setSelectedThemeItem({
      label: themeItem[0].label,
      value: themeItem[0].tid,
      title: themeItem[0].title,
    });

    setSelectedTheme(parseInt(tid, 10));
    setThemeChanged(true);
  };

  const handleCategorySelection = (tid) => {
    setSelectedCategory(String(tid));
    setOpen(false);
  };

  function handleDragEnd(ev) {
    setX((x += ev.delta.x));
    setY((y += ev.delta.y));
  }

  const menuProps = {
    anchorOrigin: {
      vertical: "top",
      horizontal: "left",
    },
    transformOrigin: {
      vertical: "top",
      horizontal: "right",
    },
  };

  const checkLibrarySlide = (slides) => {
    let librarySlides = [];
    let regularSlides = [];
    let librarySlideIndices = [];
    let regularSlideIndices = [];

    if (slides && slides.length > 0) {
      const { selectedSlides } = presentation?.slides.reduce(
        (arr, e, index) => {
          if (slides.includes(e.id)) {
            arr.selectedSlides.push({ ...e, index: index + 1 });
          }
          return arr;
        },
        { selectedSlides: [] },
      );

      selectedSlides.forEach((e) => {
        if (e.initial_presentation !== presentation?.id) {
          librarySlides.push(e.id);
          librarySlideIndices.push(e.index);
        } else {
          regularSlides.push(e.id);
          regularSlideIndices.push(e.index);
        }
      });
    }

    if (regularSlides.length > 0) {
      toggleSlideStatus(regularSlides);
    }
    if (librarySlides.length > 0) {
      if (regularSlides.length > 0) {
        setRegularIndices(regularSlideIndices);
      }
      setLibraryIndices(librarySlideIndices);
      setLibrarySlidesToRemove(librarySlides);
    }
  };

  const toggleLibraryPrompt = () => {
    let regIndices = regularIndices.length > 1;
    let libIndices = libraryIndices.length > 1;
    return (
      <>
        {regularIndices.length > 0 ? (
          <>
            {`Slide${regIndices ? "s" : ""}: ${regularIndices.join(", ")} ${
              regIndices ? "have" : "has"
            } been hidden.`}{" "}
            <br />
            <br />
            {`However, your selection also includes the following 'COPY' slide${
              libIndices ? "s" : ""
            } from the library: ${libraryIndices.join(", ")}.`}{" "}
            <br />
            <br />
          </>
        ) : (
          <>
            {`You have selected the following 'COPY' slide${
              libIndices ? "s" : ""
            } from the library: ${libraryIndices.join(", ")}.`}{" "}
            <br />
            <br />
          </>
        )}
        These slides cannot be hidden. Would you like to remove them from the
        presentation instead?
        <br />
        <br />
        N.B. You can add them back at any time.
      </>
    );
  };
  const processEmptyNotes = (_notesObj) => {
    if (_notesObj !== null) {
      if (Object.keys(_notesObj).length > 0) {
        let notesObjKeys = Object.keys(_notesObj);
        let tempNotesObj = { ..._notesObj };
        notesObjKeys.forEach((e) => {
          if (tempNotesObj[e] === "" || tempNotesObj[e] === null) {
            delete tempNotesObj[e];
          }
        });
        return tempNotesObj;
      } else return null;
    } else return null;
  };

  const allCategoryValues = React.useMemo(() => {
    // only include real taxonomy IDs, skip 0 because "All Categories" is added manually
    return categories.map((c) => c.value);
  }, [categories]);

  const renderMenuItems = (parentId = null) => {
    const items = categories.filter(
      (item) => item.parent_target_id === parentId,
    );

    return items.map((item) => {
      const children = categories.filter(
        (child) => child.parent_target_id === item.value,
      );

      if (children.length > 0) {
        if (!item.has_create_permission) {
          return null;
        }

        return (
          <NestedMenuItem
            key={`cat-${item.value}`}
            leftIcon={<ChevronLeft />}
            rightIcon={null}
            parentMenuOpen={open}
            label={decodeHtmlEntities(item.label)}
            MenuProps={menuProps}
            className="settings-nested"
            onClick={() => handleCategorySelection(item.value)}
          >
            {renderMenuItems(item.value)}
          </NestedMenuItem>
        );
      }

      if (!item.has_create_permission) {
        return null;
      }

      return (
        <MenuItem
          key={`cat-${item.value}`}
          value={item.value}
          sx={{
            fontSize: "16px",
            fontWeight: "400",
            "&.Mui-selected": {
              backgroundColor: "primary.light",
            },
          }}
          onClick={() => handleCategorySelection(item.value)}
        >
          {decodeHtmlEntities(item.label)}
        </MenuItem>
      );
    });
  };

  return (
    <main className="edit-presentation screen">
      <DndContext
        onDragEnd={handleDragEnd}
        sensors={sensors}
        modifiers={[restrictToParentElement]}
      >
        <SlideContextProvider>
          <Container ref={setNodeRef} fluid className="ps-container h-100">
            {(isLoading || configLoading) && (
              <Box className="circular-container fs">
                <CircularProgress size={20} />
              </Box>
            )}

            {isPublishing && (
              <Box
                className="circular-container fs"
                sx={{
                  color: "#000",
                  flexDirection: "column",
                  gap: 2,
                  textAlign: "center",
                  px: 3,
                  backgroundColor: "rgba(255, 255, 255, 1) !important",
                }}
              >
                <CircularProgress size={20} />
                <Typography variant="h6">Publishing…</Typography>
                <Typography variant="body2">
                  This presentation is locked until publishing finishes.
                </Typography>
              </Box>
            )}

            {authorised ? (
              <Row className="ps-row h-100 justify-content-center align-self-center">
                <Col xs={"auto"} className="ps-col ps-sidebar">
                  {isError ? (
                    <Box
                      className="d-flex h-100 justify-content-center align-items-center align-content-center"
                      sx={{ flexDirection: "column" }}
                    >
                      <Alert variant="filled" severity="error">
                        An error occurred.
                      </Alert>
                    </Box>
                  ) : (
                    <>
                      {presentation ? (
                        <Sidebar
                          user={user}
                          csrf_token={csrf_token}
                          presentation={presentation}
                          samplePresentation={samplePresentation}
                          // @TODO sample presentation loading
                          samplePresentationLoading={samplePresentationLoading}
                          permissions={permissions}
                          categories={categories}
                          themes={themes}
                          languages={languages}
                          indexh={indexh}
                          navigateTo={navigateTo}
                          isPublishing={isPublishing}
                          onPublishSlides={(slides) => onPublishSlides(slides)}
                          onAddSlide={(slide, layout, _index) => {
                            addSlide(slide, layout, _index);
                          }}
                          onAddLibrarySlides={(
                            slides,
                            _index,
                            notes,
                            slideAudio,
                          ) => {
                            addLibrarySlides(slides, _index, notes, slideAudio);
                          }}
                          onAddCloneSlides={(
                            slides,
                            _index,
                            notes,
                            slideAudio,
                          ) => {
                            addCloneSlides(slides, _index, notes, slideAudio);
                          }}
                          onAddTranslatedSlides={(
                            slides,
                            _language,
                            _index,
                            notes,
                            slideAudio,
                          ) => {
                            addTranslatedSlides(
                              slides,
                              _language,
                              _index,
                              notes,
                              slideAudio,
                            );
                          }}
                          onRemoveSlides={(slides) => {
                            setSlidesToRemove(slides);
                          }}
                          onRenameSlide={(slideId, newTitle) =>
                            renameSlide(slideId, newTitle)
                          }
                          reorderSlidesQuietly={(newOrderIds, newActiveIndex) =>
                            reorderSlidesQuietly(newOrderIds, newActiveIndex)
                          }
                          toggleStatus={(slides) => {
                            checkLibrarySlide(slides);
                          }}
                          generateThumbnails={(slides) => {
                            generateThumbnails(slides);
                          }}
                          thumbnailVersions={thumbnailVersions}
                          onDuplicateSlides={(
                            slides,
                            copies,
                            notes,
                            slideAudio,
                          ) => {
                            const count = Number(copies);
                            const slide = slides[0];

                            const slideIndex = presentation.slides.findIndex(
                              (s) => s.id === slide,
                            );

                            const slidesToCreate = new Array(count).fill(
                              slides[0],
                            );

                            addCloneSlides(
                              slidesToCreate,
                              slideIndex + 1,
                              notes,
                              slideAudio,
                              true,
                            );
                          }}
                        />
                      ) : (
                        <Box
                          className="d-flex h-100 justify-content-center align-items-center align-content-center"
                          sx={{ flexDirection: "column" }}
                        >
                          <CircularProgress size={20} />
                        </Box>
                      )}
                    </>
                  )}
                </Col>
                <Col className="ps-col ps-viewer h-100">
                  <Toolbar
                    isError={isError}
                    refreshPresentation={getContent}
                    permissions={permissions}
                    csrf_token={csrf_token}
                    userAuth={userAuth}
                    onPresentationModal={() => {
                      setShowPresentationModal(true);
                    }}
                    onPresentationAudioModal={() => {
                      setShowPresentationAudioModal(true);
                    }}
                    onLogoSettings={() => {
                      setShowLogoSettings(true);
                    }}
                    onBackgroundSettings={() => {
                      setShowBackgroundSettings(true);
                    }}
                    onTransitionSettings={() => {
                      setShowTransitionSettings(true);
                    }}
                    hasPresentationAudio={audio ? true : false}
                    presentation={presentation}
                    reveal={reveal}
                    indexh={indexh}
                    onRemoveSlides={(slides) => {
                      setSlidesToRemove(slides);
                    }}
                    onSlideSettings={() => {
                      setShowSlideSettings(true);
                    }}
                    onSlideNotes={() => {
                      setShowSlideNotes(true);
                    }}
                    onSlideAudio={() => {
                      setShowSlideAudioSettings(true);
                    }}
                    goBack={() => {
                      navigate("/presentations-creator");
                    }}
                    toggleStatus={(slides) => {
                      toggleSlideStatus(slides);
                    }}
                    corruptSlides={corruptSlides}
                  />

                  <div
                    style={
                      backgroundColor
                        ? { backgroundColor: backgroundColor }
                        : {}
                    }
                    className={`edit-container ${presentation ? "" : "h-100"}`}
                  >
                    {/* <BlockTree /> */}
                    <div className="reveal">
                      <div className="slides">
                        {isError ? (
                          <Box
                            className="d-flex h-100 justify-content-center align-items-center align-content-center"
                            sx={{ flexDirection: "column" }}
                          >
                            <Alert variant="filled" severity="error">
                              An error occurred. Please try again.
                            </Alert>
                          </Box>
                        ) : (
                          <>
                            {presentation?.slides.length > 0 ? (
                              <>
                                {presentation?.slides?.map((slide, index) => {
                                  return (
                                    <SlideEdit
                                      key={`slide-${slide.id}`}
                                      user={user}
                                      userAuth={userAuth}
                                      csrf_token={csrf_token}
                                      permissions={permissions}
                                      slide={slide}
                                      presentation={presentation}
                                      themes={themes}
                                      selectedTheme={selectedTheme}
                                      onRevealUpdate={() => {
                                        reveal.sync();
                                      }}
                                      onSlideUpdate={() => {
                                        getContent();
                                      }}
                                      onSlideSaved={(slideId) => {
                                        pollThumbnailRefresh([slideId]);
                                      }}
                                      onSettingsModalHide={() => {
                                        setShowSlideLayoutSettings(false);
                                        setShowLogoSettings(false);
                                        setShowBackgroundSettings(false);
                                        setShowTransitionSettings(false);
                                        setShowSlideSettings(false);
                                        setShowHelpDialog(false);
                                      }}
                                      onOverrideModalHide={() => {
                                        setShowSlideAudioSettings(false);
                                        setShowSlideNotes(false);
                                      }}
                                      setSlideAudioSettings={(condition) => {
                                        setShowSlideAudioSettings(condition);
                                      }}
                                      activeTrigger={
                                        index === indexh ? true : false
                                      }
                                      showSlideAudioSettings={
                                        index === indexh
                                          ? showSlideAudioSettings
                                          : false
                                      }
                                      showLayoutSettings={
                                        index === indexh
                                          ? showSlideLayoutSettings
                                          : false
                                      }
                                      showLogoSettings={
                                        index === indexh
                                          ? showLogoSettings
                                          : false
                                      }
                                      showBackgroundSettings={
                                        index === indexh
                                          ? showBackgroundSettings
                                          : false
                                      }
                                      showTransitionSettings={
                                        index === indexh
                                          ? showTransitionSettings
                                          : false
                                      }
                                      showSlideSettings={
                                        index === indexh
                                          ? showSlideSettings
                                          : false
                                      }
                                      showHelpDialog={
                                        index === indexh
                                          ? showHelpDialog
                                          : false
                                      }
                                      showSlideNotes={
                                        index === indexh
                                          ? showSlideNotes
                                          : false
                                      }
                                      onCorruptJson={(id) => {
                                        setCorruptsSlides((prevSlides) => {
                                          if (!prevSlides.includes(id)) {
                                            return [...prevSlides, id];
                                          }
                                          return prevSlides;
                                        });
                                      }}
                                      slideNotes={
                                        notesObj?.hasOwnProperty(slide.id)
                                          ? notesObj[slide.id]
                                          : null
                                      }
                                      onSlideNotesUpdate={(id, value) => {
                                        let tempNotesObj = { ...notesObj };
                                        if (value === null || value === "") {
                                          if (tempNotesObj.hasOwnProperty(id)) {
                                            delete tempNotesObj[id];
                                          }
                                        } else {
                                          tempNotesObj[id] = value;
                                        }
                                        // Optimistic local update + skip the full
                                        // refetch so only this slide re-renders.
                                        setNotesObj(
                                          processEmptyNotes(tempNotesObj) ?? {},
                                        );
                                        updatePresentation(
                                          null,
                                          tempNotesObj,
                                          null,
                                          { skipRefetch: true },
                                        );
                                      }}
                                      slideAudioData={
                                        slideAudio?.hasOwnProperty(slide.id)
                                          ? slideAudio[slide.id]
                                          : null
                                      }
                                      onSlideAudioUpdate={(id, data) => {
                                        let tempSlideAudio = {
                                          ...slideAudio,
                                        };
                                        if (data === null || data === "") {
                                          if (
                                            tempSlideAudio.hasOwnProperty(id)
                                          ) {
                                            delete tempSlideAudio[id];
                                          }
                                        } else {
                                          tempSlideAudio[id] = { ...data };
                                        }
                                        // Optimistic local update + skip the full
                                        // refetch so only this slide re-renders.
                                        setSlideAudio(tempSlideAudio);
                                        updatePresentation(
                                          null,
                                          null,
                                          tempSlideAudio,
                                          { skipRefetch: true },
                                        );
                                      }}
                                    />
                                  );
                                })}
                              </>
                            ) : (
                              <section className="slide-wrap h-100">
                                <div className="d-flex h-100 justify-content-center align-items-center">
                                  {presentation ? (
                                    <>
                                      {presentation?.slides.length < 1 && (
                                        <Typography
                                          variant="h5"
                                          owner="admin"
                                          color="adminText.contrastText"
                                        >
                                          To get started, please click '+ Add
                                          Slide' in the sidebar to create your
                                          first slide.
                                        </Typography>
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      {isError ? (
                                        <Alert
                                          variant="filled"
                                          severity="error"
                                        >
                                          An error occurred. Please try again.
                                        </Alert>
                                      ) : (
                                        <CircularProgress size={20} />
                                      )}
                                    </>
                                  )}
                                </div>
                              </section>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            ) : (
              <Row className="ps-row h-100 justify-content-center align-self-center">
                <Col>
                  <Box
                    className="d-flex h-100 justify-content-center align-items-center align-content-center"
                    sx={{ flexDirection: "column" }}
                  >
                    <Alert variant="filled" severity="error">
                      You don't have permission to edit this presentation.
                    </Alert>
                    <Button
                      className="mt-3"
                      variant="outlined"
                      startIcon={<ArrowBack />}
                      onClick={() => {
                        // if (reveal) {
                        //   reveal.destroy();
                        // }
                        navigate("/presentations-creator");
                      }}
                    >
                      Back
                    </Button>
                  </Box>
                </Col>
              </Row>
            )}
            {authorised && presentation && (
              <>
                {!isError && (
                  <FloatingRevealControls
                    x={x}
                    y={y}
                    elementDimensions={elementDimensions}
                    setElementDimensions={setElementDimensions}
                    isInitial={isInitial}
                    setIsInitial={setIsInitial}
                    setX={setX}
                    reveal={reveal}
                    indexh={indexh}
                    presentation={presentation}
                    showControls={showControls}
                    editMode={true}
                    showSlideAudioControls={(value) => {
                      switch (value) {
                        case 0:
                          setShowSlideAudioControls(false);
                          break;
                        case 1:
                          setShowSlideAudioControls(!showSlideAudioControls);
                          break;
                        case 2:
                          setShowSlideAudioControls(true);
                          break;
                        case 3:
                          setShowSlideAudioControls(false);
                          break;
                        default:
                          // do nothing
                          break;
                      }
                    }}
                    playSlideAudio={() => {
                      setPlaySlideAudio(!playSlideAudio);
                    }}
                    slideAudioActive={false}
                    hasPresentationAudio={audio}
                    showPresentationAudioControls={(value) => {
                      setShowPresentationAudioControls(value);
                    }}
                    playPresentationAudio={() => {
                      setPlayPresentationAudio(!playPresentationAudio);
                    }}
                    presentationAudioActive={presentationAudioPlaying}
                    onHelp={() => {
                      setShowHelpDialog(true);
                    }}
                    presentationAudioObject={{
                      ...audio,
                      playOverride: playPresentationAudio,
                      showControls: showPresentationAudioControls,
                      editMode: true,
                    }}
                    presentationAudioPlaying={(value) => {
                      setPresentationAudioPlaying(value);
                    }}
                    hideSlideAudioControls={() =>
                      setShowPresentationAudioControls(false)
                    }
                  />
                )}
              </>
            )}
          </Container>
        </SlideContextProvider>
      </DndContext>

      <Dialog
        className="ps-modal presentation-settings-modal"
        open={showPresentationModal}
        maxWidth={"lg"}
        fullWidth={true}
        onClose={(event, reason) => {
          if (reason && reason === "backdropClick") return;
          setShowPresentationModal(false);
        }}
        PaperComponent={DialogPaper}
      >
        <Box className="ps-modal-title dialog-draggable-handle">
          <Container fluid>
            <Row className="align-items-center">
              <Col xs={12} sm={6}>
                <Typography variant="h3" owner="admin" color="adminText.dark">
                  Presentation settings
                </Typography>
              </Col>
              <Col xs={12} sm={6}>
                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  alignItems="center"
                  spacing={1}
                >
                  <Button
                    variant="outlined"
                    owner="admin"
                    dialogbtn="true"
                    dialogvariant="cancel"
                    onClick={() => {
                      setShowPresentationModal(false);
                    }}
                  >
                    <Typography variant="button" owner="admin" className="lh">
                      Cancel
                    </Typography>
                  </Button>
                  <Button
                    variant="contained"
                    owner="admin"
                    dialogbtn="true"
                    dialogvariant="save"
                    className="lh"
                    onClick={() => {
                      setLoading(true);
                      setShowPresentationModal(false);
                      updatePresentation();
                    }}
                  >
                    <Typography variant="button" owner="admin" className="lh">
                      Save
                    </Typography>
                  </Button>
                </Stack>
              </Col>
            </Row>
          </Container>
        </Box>
        <DialogContent>
          <Box className="ps-modal-content">
            <Container fluid>
              <Row>
                <Col xs={12} sm={6}>
                  <Box className="spacer ls">
                    <Box className="form-group d-flex flex-column">
                      <Typography
                        className="vertical"
                        variant="form-group-label"
                        htmlFor="presentation-title"
                        owner="admin"
                      >
                        Title
                      </Typography>
                      <TextField
                        id="presentation-title"
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={title}
                        sx={{
                          "& .MuiInputBase-input": {
                            height: 28,
                            // paddingTop: '11px',
                            // paddingBottom: '11px'
                          },
                        }}
                        onInput={(event) => {
                          setTitle(event.target.value);
                        }}
                      />
                    </Box>
                    <Box className="form-group d-flex flex-column">
                      <Typography
                        className="vertical"
                        variant="form-group-label"
                        htmlFor="description"
                        owner="admin"
                      >
                        Description
                      </Typography>
                      <TextField
                        id="description"
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={body}
                        sx={{
                          "& .MuiInputBase-root": {
                            minHeight: 45,
                            paddingTop: "3px",
                            paddingBottom: "3px",
                          },
                        }}
                        onInput={(event) => {
                          setBody(event.target.value);
                        }}
                        multiline
                        maxRows={3}
                      />
                    </Box>
                    {permissions.access_promote_to_core_library && (
                      <Box className="form-group">
                        <FormControlLabel
                          control={
                            <Checkbox
                              id="core-lib-checkbox"
                              checked={coreLibrary}
                              onChange={(event) => {
                                if (event.target.checked) {
                                  setCoreLibrary(true);
                                } else {
                                  setCoreLibrary(false);
                                }
                              }}
                            />
                          }
                          label={
                            <Typography
                              variant="form-group-label"
                              owner="admin"
                            >
                              Promote to Corporate library
                            </Typography>
                          }
                        />
                      </Box>
                    )}
                    <div className="form-group multi-group">
                      <FormControlLabel
                        control={
                          <Checkbox
                            id="home-slide-checkbox"
                            checked={homeSlide ? true : false}
                            onChange={(event) => {
                              if (event.target.checked) {
                                setHomeSlide(homeSlide ? homeSlide : 1);
                                setShowHomeSlideInput(true);
                              } else {
                                setHomeSlide("");
                                setShowHomeSlideInput(false);
                              }
                            }}
                          />
                        }
                        label={
                          <Typography variant="form-group-label" owner="admin">
                            Show home slide button / Slide #
                          </Typography>
                        }
                      />
                      {showHomeSlideInput && (
                        <div className="inner-form-group">
                          <TextField
                            id="slideNumber"
                            size="small"
                            value={homeSlide}
                            type="number"
                            InputProps={{
                              inputProps: {
                                min: 1,
                                max: presentation?.slides?.length,
                              },
                            }}
                            onChange={(event) => {
                              setHomeSlide(event.target.value);
                            }}
                          />
                        </div>
                      )}
                    </div>
                    <Box className="form-group pres-bg  d-flex flex-column">
                      <Typography
                        className="vertical"
                        variant="form-group-label"
                        owner="admin"
                      >
                        Presentation background
                      </Typography>
                      <ColorPickerNew
                        hideTitle={true}
                        slideBackgroundColor={
                          backgroundColor ? backgroundColor : ""
                        }
                        colors={getThemeBackgrounds(presentation?.theme?.label)}
                        hideDefault={true}
                        onColorSelected={(background_color) => {
                          setBackgroundColor(background_color);
                        }}
                      />
                    </Box>
                  </Box>
                </Col>
                <Col xs={12} sm={6}>
                  <Box className="spacer rs">
                    <Box className="form-group  d-flex flex-column">
                      <Typography
                        className="vertical"
                        variant="form-group-label"
                        htmlFor="themes"
                        owner="admin"
                      >
                        Theme
                      </Typography>
                      <CategorySelector
                        name="presentationThemes"
                        label="Theme"
                        categories={themes}
                        selectedCategory={
                          selectedThemeItem?.value === 0
                            ? { label: "Select a theme", value: 0 }
                            : selectedThemeItem
                        }
                        outlined={true}
                        variant="outlined"
                        size="small"
                        handleCategorySelection={(tid) => {
                          handleThemeSelection(tid);
                        }}
                        disableAllValue={true}
                      />
                    </Box>
                    <Box className="form-group d-flex flex-column">
                      <Typography
                        className="vertical"
                        variant="form-group-label"
                        htmlFor="presentationCategories"
                        owner="admin"
                      >
                        Category
                      </Typography>
                      <FormControl
                        variant={"outlined"}
                        size={"small"}
                        sx={{
                          width: 1,
                          "& label[data-shrink=true]": {
                            visibility: "hidden",
                          },
                          "& legend": {
                            width: 0,
                          },
                        }}
                      >
                        <InputLabel id={`category`}>Category</InputLabel>
                        <Select
                          labelId="category-select-label"
                          id="category-select"
                          value={selectedCategory}
                          label="Category"
                          open={open}
                          onClose={() => setOpen(false)}
                          onOpen={() => setOpen(true)}
                          renderValue={(selected) => {
                            const stringified = String(selected);

                            if (!stringified || stringified === "0")
                              return "All Categories";
                            const found = categories.find(
                              (c) => c.value === stringified,
                            );

                            return found
                              ? decodeHtmlEntities(found.label)
                              : "All Categories";
                          }}
                        >
                          {allCategoryValues.map((id) => (
                            <MenuItem
                              key={`register-${id}`}
                              value={id}
                              sx={{ display: "none" }}
                            />
                          ))}

                          <MenuItem
                            key="all-categories"
                            value={0}
                            sx={{
                              fontSize: "16px",
                              fontWeight: "400",
                              "&.Mui-selected": {
                                backgroundColor: "primary.light",
                              },
                            }}
                            disabled
                          >
                            All Categories
                          </MenuItem>

                          {renderMenuItems(null)}
                        </Select>

                        {/* <Select
                          labelId={`category-id`}
                          value={selectedCategory}
                          label={"Category"}
                          open={open}
                          onClose={() => setOpen(false)}
                          onOpen={() => setOpen(true)}
                        >
                          {categories?.map((category, index) => {
                            const has_child = hasChildren(category.value);
                            const is_top_level = category.parent_target_id
                              ? false
                              : true;

                            if (
                              !category.has_create_permission &&
                              category.value !== 0
                            ) {
                              return null;
                            }

                            if (is_top_level) {
                              if (has_child) {
                                const region_child_items = getChildItems(
                                  categories,
                                  category.value
                                );

                                return [
                                  <MenuItem
                                    key={`category-${category.value}`}
                                    value={category.value}
                                    sx={{ display: "none" }}
                                    onClick={() => {
                                      handleCategorySelection(category.value);
                                    }}
                                  >
                                    {decodeHtmlEntities(category.label)}
                                  </MenuItem>,
                                  <NestedMenuItem
                                    key={`category-${category.value}`}
                                    leftIcon={<ChevronLeft />}
                                    rightIcon={null}
                                    parentMenuOpen={open}
                                    label={category.label}
                                    MenuProps={menuProps}
                                    className="settings-nested"
                                    onClick={() => {
                                      handleCategorySelection(category.value);
                                    }}
                                  >
                                    {region_child_items.map((region) => {
                                      const countries = getChildItems(
                                        categories,
                                        region.value
                                      );

                                      return (
                                        <NestedMenuItem
                                          key={`region-${region.value}`}
                                          leftIcon={<ChevronLeft />}
                                          rightIcon={null}
                                          parentMenuOpen={open}
                                          label={region.label}
                                          MenuProps={menuProps}
                                          className="settings-nested"
                                          onClick={() => {
                                            handleCategorySelection(
                                              region.value
                                            );
                                          }}
                                        >
                                          {countries.map((country) => {
                                            const cities = getChildItems(
                                              categories,
                                              country.value
                                            );

                                            return (
                                              <NestedMenuItem
                                                key={`country-${country.value}`}
                                                leftIcon={<ChevronLeft />}
                                                rightIcon={null}
                                                parentMenuOpen={open}
                                                label={country.label}
                                                MenuProps={menuProps}
                                                className="settings-nested"
                                                onClick={() => {
                                                  handleCategorySelection(
                                                    country.value
                                                  );
                                                }}
                                              >
                                                {cities.map(
                                                  (city, cityIndex) => {
                                                    return (
                                                      <MenuItem
                                                        key={`city-${city.value}`}
                                                        value={city.value}
                                                        sx={{
                                                          fontSize: "16px",
                                                          fontWeight: "400",
                                                          "&.Mui-selected": {
                                                            backgroundColor:
                                                              "primary.light",
                                                          },
                                                        }}
                                                        onClick={() => {
                                                          handleCategorySelection(
                                                            city.value
                                                          );
                                                        }}
                                                      >
                                                        {decodeHtmlEntities(
                                                          city.label
                                                        )}
                                                      </MenuItem>
                                                    );
                                                  }
                                                )}
                                              </NestedMenuItem>
                                            );
                                          })}
                                        </NestedMenuItem>
                                      );
                                    })}
                                  </NestedMenuItem>,
                                ];
                              } else {
                                return (
                                  <MenuItem
                                    key={`category-${category.value}`}
                                    value={category.value}
                                    sx={{
                                      fontSize: "16px",
                                      fontWeight: "400",
                                      "&.Mui-selected": {
                                        backgroundColor: "primary.light",
                                      },
                                    }}
                                    onClick={() => {
                                      handleCategorySelection(category.value);
                                    }}
                                  >
                                    {decodeHtmlEntities(category.label)}
                                  </MenuItem>
                                );
                              }
                            } else {
                              return (
                                <MenuItem
                                  key={`category-${category.value}`}
                                  value={category.value}
                                  sx={{ display: "none" }}
                                  onClick={() => {
                                    handleCategorySelection(category.value);
                                  }}
                                >
                                  {decodeHtmlEntities(category.label)}
                                </MenuItem>
                              );
                            }
                          })}
                        </Select> */}
                      </FormControl>
                    </Box>
                    <Box className="form-group d-flex flex-column">
                      <Typography
                        className="vertical"
                        variant="form-group-label"
                        htmlFor="languages"
                        owner="admin"
                      >
                        Language
                      </Typography>
                      <FormControl
                        variant={"outlined"}
                        size={"small"}
                        sx={{
                          width: 1,
                          "& label[data-shrink=true]": {
                            visibility: "hidden",
                          },
                          "& legend": {
                            width: 0,
                          },
                        }}
                      >
                        <InputLabel id={`Language`}>Language</InputLabel>
                        <Select
                          labelId={`language-id`}
                          value={selectedLanguage}
                          label={"Language"}
                          onChange={(event) => {
                            setSelectedLanguage(event.target.value);
                          }}
                        >
                          {languages?.map((language, index) => {
                            return (
                              <MenuItem
                                key={`language-${language.value}`}
                                value={language.value}
                                sx={{ fontSize: 16 }}
                                disabled={
                                  language.value === 0 || language.value === "0"
                                }
                              >
                                {decodeHtmlEntities(language.label)}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </Box>
                    {sitaEnabled && (
                      <Box className="form-group d-flex flex-column">
                        <Typography
                          className="vertical"
                          variant="form-group-label"
                          htmlFor="sita"
                          owner="admin"
                        >
                          SITA
                        </Typography>
                        <FormControl
                          fullWidth
                          size="small"
                          sx={{
                            width: 1,
                            "& label[data-shrink=true]": {
                              visibility: "hidden",
                            },
                            "& legend": {
                              width: 0,
                            },
                          }}
                        >
                          <Autocomplete
                            options={sitas}
                            getOptionLabel={(option) => option.title}
                            value={selectedSita ? selectedSita : null}
                            disableClearable={true}
                            onChange={(event, value) => {
                              setSelectedSita(value);
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="SITA"
                                InputProps={{
                                  ...params.InputProps,
                                  type: "search",
                                  sx: {
                                    paddingBottom: 0,
                                    "& .MuiOutlinedInput-input": {
                                      paddingBottom: "0 !important",
                                      paddingTop: "0 !important",
                                      borderColor: "#c2ccd6",
                                    },
                                    "&:hover .MuiOutlinedInput-input": {
                                      borderWidth: "2px",
                                      borderColor: "primary.main",
                                    },
                                    "& .MuiAutocomplete-endAdornment": {
                                      // top: "calc(50% - 16px)",
                                    },
                                  },
                                }}
                              />
                            )}
                          />
                        </FormControl>
                      </Box>
                    )}

                    {referenceEnabled && (
                      <Box className="form-group d-flex flex-column">
                        <Typography
                          className="vertical"
                          variant="form-group-label"
                          htmlFor="reference"
                          owner="admin"
                        >
                          Reference
                        </Typography>
                        <FormControl
                          fullWidth
                          size="small"
                          sx={{
                            width: 1,
                            "& label[data-shrink=true]": {
                              visibility: "hidden",
                            },
                            "& legend": {
                              width: 0,
                            },
                          }}
                        >
                          <Autocomplete
                            options={references}
                            getOptionLabel={(option) => option.title}
                            value={selectedReference ? selectedReference : null}
                            disableClearable={true}
                            onChange={(event, value) => {
                              setSelectedReference(value);
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Reference"
                                InputProps={{
                                  ...params.InputProps,
                                  type: "search",
                                  sx: {
                                    paddingBottom: 0,
                                    "& .MuiOutlinedInput-input": {
                                      paddingBottom: "0 !important",
                                      paddingTop: "0 !important",
                                      borderColor: "#c2ccd6",
                                    },
                                    "&:hover .MuiOutlinedInput-input": {
                                      borderWidth: "2px",
                                      borderColor: "primary.main",
                                    },
                                    "& .MuiAutocomplete-endAdornment": {
                                      // top: "calc(50% - 16px)",
                                    },
                                  },
                                }}
                              />
                            )}
                          />
                        </FormControl>
                      </Box>
                    )}
                  </Box>
                </Col>
              </Row>
              {permissions.access_advanced_presentation_settings && (
                <Row>
                  <Accordion
                    elevation={0}
                    sx={{
                      "&:before": {
                        display: "none",
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<KeyboardArrowRight />}
                      sx={{
                        padding: 0,
                        flexDirection: "row-reverse",
                        "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded":
                          {
                            transform: "rotate(90deg)",
                          },
                      }}
                    >
                      <Col xs={12} className="d-flex align-items-center">
                        Advanced
                        <Divider className="mt-1 ms-2 flex-grow-1" />
                      </Col>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{
                        paddingLeft: 0,
                        paddingRight: 0,
                      }}
                    >
                      <Row className="slide-settings-adv">
                        <Col xs={12} sm={6}>
                          <BespokeMedia
                            user={user}
                            files={{
                              minWidth: 400,
                              minHeight: 225,
                              maxWidth: 400,
                              maxHeight: 225,
                              aspectRatio: 1.777777777777778,
                            }}
                            media={thumbnail}
                            onMediaInsert={(media) => {
                              setThumbnail(media);
                            }}
                            title={`Bespoke presentation thumbnail`}
                            hint={`A thumbnail for the presentation will be created automatically. However, if you would like to create your own, please upload an image file via the ${
                              client === "radisson"
                                ? "Media library"
                                : "Asset Library"
                            }.`}
                          />
                        </Col>
                      </Row>
                    </AccordionDetails>
                  </Accordion>
                </Row>
              )}
            </Container>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        className="ps-modal presentation-settings-modal presentation-audio-modal"
        open={showPresentationAudioModal}
        maxWidth={"md"}
        fullWidth={true}
        onClose={(event, reason) => {
          if (reason && reason === "backdropClick") return;
          setShowPresentationAudioModal(false);
        }}
        PaperComponent={DialogPaper}
      >
        <Box className="ps-modal-title dialog-draggable-handle">
          <Container fluid>
            <Row className="align-items-center">
              <Col xs={12} sm={6}>
                <Typography variant="h3" owner="admin" color="adminText.dark">
                  Presentation audio
                </Typography>
              </Col>
              <Col xs={12} sm={6}>
                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  alignItems="center"
                  spacing={1}
                >
                  <Button
                    variant="outlined"
                    owner="admin"
                    dialogbtn="true"
                    dialogvariant="cancel"
                    onClick={() => {
                      setShowPresentationAudioModal(false);
                    }}
                  >
                    <Typography variant="button" owner="admin" className="lh">
                      Cancel
                    </Typography>
                  </Button>
                  <Button
                    variant="contained"
                    owner="admin"
                    dialogbtn="true"
                    dialogvariant="save"
                    className="lh"
                    onClick={() => {
                      setLoading(true);
                      setShowPresentationAudioModal(false);
                      updatePresentation();
                    }}
                  >
                    <Typography variant="button" owner="admin" className="lh">
                      Save
                    </Typography>
                  </Button>
                </Stack>
              </Col>
            </Row>
          </Container>
        </Box>
        <DialogContent>
          <Box className="">
            <Container fluid>
              <AddAudio
                presentationSetting={true}
                user={user}
                audioData={audio}
                onAudioChange={(audio) => {
                  setAudio(audio);
                }}
              />
            </Container>
          </Box>
        </DialogContent>
      </Dialog>

      {/* <Dialog open={slidesToRemove.length > 0 ? true : false} onClose={() => setSlidesToRemove([])}>
        <DialogTitle>Remove slides</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you would like to remove the selected slides from this presentation?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSlidesToRemove([])}>Cancel</Button>
          <Button color='error' onClick={() => {
            setLoading(true);
            removeSlides(slidesToRemove);
            setSlidesToRemove([]);
          }}>Remove</Button>
        </DialogActions>
      </Dialog> */}
      <AlertModal
        showAlert={slidesToRemove.length > 0 ? true : false}
        showAlertCallback={() => setSlidesToRemove([])}
        showCaLogo={true}
        alertMessageTitle={"Remove slides?"}
        alertMessageBody={
          "Are you sure you want to remove the selected slide(s) from this presentation?"
        }
        cancelButton={true}
        confirmButton={true}
        cancelButtonLabel={"Cancel"}
        confirmButtonLabel={"Remove"}
        confirmButtonColor={"error"}
        sx={{ zIndex: 1450 }}
        onConfirm={() => {
          setLoading(true);
          removeSlides(slidesToRemove);
          setSlidesToRemove([]);
        }}
      />
      <AlertModal
        showAlert={librarySlidesToRemove.length > 0 ? true : false}
        showAlertCallback={() => {
          setRegularIndices([]);
          setLibraryIndices([]);
          setLibrarySlidesToRemove([]);
        }}
        showCaLogo={true}
        alertMessageTitle={"Warning"}
        alertMessageBody={toggleLibraryPrompt()}
        cancelButton={true}
        confirmButton={true}
        cancelButtonLabel={"Cancel"}
        confirmButtonLabel={"Remove"}
        confirmButtonColor={"error"}
        sx={{ zIndex: 1450 }}
        onConfirm={() => {
          setLoading(true);
          removeSlides(librarySlidesToRemove);
          setRegularIndices([]);
          setLibraryIndices([]);
          setLibrarySlidesToRemove([]);
        }}
      />
      <AlertModal
        showAlert={excludeAlert}
        showAlertCallback={() => {
          setExcludeIndices([]);
          setExcludeAlert(false);
        }}
        showCaLogo={true}
        alertMessageTitle={"Warning"}
        alertMessageBody={`The theme for the following slide(s): ${excludeIndices.join(
          ", ",
        )} was not changed because the template is not available in the selected theme, or the slides are uneditable copies from the library.`}
        confirmButton={true}
        confirmButtonLabel={"Confirm"}
        confirmButtonColor={"primary"}
        onConfirm={() => {
          window.location.reload();
        }}
      />

      <Snackbar
        open={addSlideError ? true : false}
        autoHideDuration={3000}
        onClose={() => {
          setAddSlideError(false);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        message="Failed to create slide. Please try again."
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={() => {
              setAddSlideError(false);
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        }
      />

      <Snackbar
        open={slidePatchError ? true : false}
        autoHideDuration={3000}
        onClose={() => {
          setSlidePatchError(false);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        message="Failed to update slide. Please try again."
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={() => {
              setSlidePatchError(false);
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        }
      />

      <Snackbar
        open={addCloneSlidesError ? true : false}
        autoHideDuration={3000}
        onClose={() => {
          setAddCloneSlidesError(false);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        message="Failed to clone slides. Please try again."
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={() => {
              setAddCloneSlidesError(false);
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        }
      />

      <Snackbar
        open={addLibrarySlidesError ? true : false}
        autoHideDuration={3000}
        onClose={() => {
          setAddLibrarySlidesError(false);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        message="Failed to add library slides. Please try again."
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={() => {
              setAddLibrarySlidesError(false);
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        }
      />

      <Snackbar
        open={removeSlidesError ? true : false}
        autoHideDuration={3000}
        onClose={() => {
          setRemoveSlidesError(false);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        message="Failed to remove slides. Please try again."
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={() => {
              setRemoveSlidesError(false);
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        }
      />

      <Snackbar
        open={publishSlidesSuccess ? true : false}
        autoHideDuration={5000}
        onClose={() => {
          setPublishSlidesSuccess(false);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          onClose={() => setPublishSlidesSuccess(false)}
          sx={{ width: "100%" }}
        >
          <Typography variant="body">
            Slide(s) published.
          </Typography>
        </Alert>
      </Snackbar>

      <Snackbar
        open={publishSlidesError ? true : false}
        autoHideDuration={5000}
        onClose={() => {
          setPublishSlidesError(false);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setPublishSlidesError(false)}
          sx={{ width: "100%" }}
        >
          {publishSlidesErrorMsg || "Failed to publish slide(s)."}
        </Alert>
      </Snackbar>
    </main>
  );
}
// eslint-disable-next-line no-lone-blocks
{
  /* {showMediaLibrary && (
        <MediaLibrary
          user={user}
          presentation={presentation}
          mediaType={mediaType}
          setShowMediaLibrary={(condition => {
            setShowMediaLibrary(condition);
          })}
          showMediaLibrary={showMediaLibrary}
          onMediaInsert={(media) => {
            if (media.bundle === 'audio') {
              setAudio({ ...media, autoPlay: false })
            }
          }}
        />
      )} */
}
